import React, { useEffect, useContext, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import mixpanel from 'mixpanel-browser';
import { HomeScreen } from './home/home';
import { SearchScreen } from './search/search';
import { CategoriesSearchScreen } from './categories/categories';
import { CategoriesResults } from './categories/categories-results';
import HandoutViewer from './handout/handout-viewer';
import HandoutPDF from './handout/pdf/handout-pdf';
import { SettingsScreen } from './settings/settings';
import { SettingsMultiUserScreen } from './settings/settings-multiuser';
import { SettingsDevScreen } from './settings/settings-dev';
import VerificationCodeInput from '../unauthenticated/onboard/verification-code-input';
import AuthorsAndReferences from './handout/authors-and-references';
import SurveyScreen from './survey/survey';
import FeedbackScreen from './feedback';
import HandoutLayout from './handout/handout-context-layout';
import StoreContext from '../../react-core-lib/components/app/store-context';
import { UnsavedChangesProvider } from './UnsavedChangesContext';
import Handouts from './settings/handouts';
import Checkout from '../checkout/checkout';
import { SnackbarProvider } from './SnackbarProvider';
import Navigation from './Navigation';
import ManageSubscriptionWrapper from './settings/manage-subscription';
import Models3DScreen from './models3D/models-3D-screen';
import MyLibrary from './handout/whiteboard/my-library';
import MyClinic from './handout/whiteboard/my-clinic';
import MyClinicWhiteboards from './handout/whiteboard/my-clinic-whiteboards';
import ArchivedWhiteboards from './handout/whiteboard/archive';
import { Modal, Box } from '@mui/material';
import PinkGradientButtonWithArrow from '../UIControls/PinkGradientButtonWithArrow';

const AuthenticatedApp = observer(() => {
    const { userDataStore } = useContext(StoreContext);
    const location = useLocation();

    const [showOnboardingModal, setShowOnboardingModal] = useState(false);

    useEffect(() => {
        const fetchUserAndTrackPageView = async () => {
            try {
                mixpanel.identify(userDataStore.userData.emailAddress);
                mixpanel.people.set({
                    $first_name: userDataStore.userData.firstName,
                    $last_name: userDataStore.userData.lastName,
                    $email: userDataStore.userData.emailAddress,
                    Mobile: userDataStore.userData.mobile,
                    'Clinic Name': userDataStore.userData.clinicName,
                    'Clinic Number': userDataStore.userData.clinicNumber,
                    'Clinic Size': userDataStore.userData.clinicSize,
                });
            } catch (error) {
                console.error('Error fetching current user for Mixpanel:', error);
            }
        };

        fetchUserAndTrackPageView();
    }, [location]);

    useEffect(() => {
        const handleShowModal = () => setShowOnboardingModal(true);
        window.addEventListener('trigger-onboarding-modal', handleShowModal);
        return () => window.removeEventListener('trigger-onboarding-modal', handleShowModal);
    }, []);

    useEffect(() => {
        const isOnboardingComplete = localStorage.getItem('onboardingComplete') !== null;
        if (isOnboardingComplete) {
            setShowOnboardingModal(true);
            localStorage.removeItem('onboardingComplete');
        }
    }, []);

    return (
        <SnackbarProvider>
            <UnsavedChangesProvider>
                <main id="main-content" style={{ zoom: document.documentElement.style.zoom || 1 }}>
                    <Navigation />
                    <Routes>
                        <Route path="/" element={<HomeScreen />} />
                        <Route path="/search" element={<SearchScreen />} />
                        <Route path="/search/categories" element={<CategoriesSearchScreen />} />
                        <Route path="/search/diagrams" element={<SearchScreen />} />
                        <Route path="/search/3dmodels" element={<SearchScreen />} />
                        <Route path="/categories-results" element={<CategoriesResults />} />
                        <Route path="/settings" element={<SettingsScreen />} />
                        <Route path="/settings-multiuser" element={<SettingsMultiUserScreen />} />
                        <Route path="/settings/manage-subscription" element={<ManageSubscriptionWrapper />} />
                        <Route path="/settings/dev" element={<SettingsDevScreen />} />
                        <Route path="/survey" element={<SurveyScreen />} />
                        <Route path="/feedback" element={<FeedbackScreen />} />
                        <Route path="/handout/:id" element={<HandoutLayout />}>
                            <Route index element={<HandoutViewer />} />
                            <Route path="pdf" element={<HandoutPDF />} />
                            <Route path="authors-and-references" element={<AuthorsAndReferences />} />
                        </Route>
                        <Route path="/code-input" element={<VerificationCodeInput />} />
                        <Route path="/handouts" element={<Handouts />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/my-library" element={<MyLibrary />} />
                        <Route path="/my-clinic" element={<MyClinic />} />
                        <Route path="/my-clinic-whiteboard/:patientId" element={<MyClinicWhiteboards />} />
                        <Route path="/archive" element={<ArchivedWhiteboards />} />
                        {/* <Route path="/3d-models" element={<Models3DScreen />} /> */}
                    </Routes>
                </main>

                <Modal
                    open={showOnboardingModal}
                    onClose={() => {}}
                    aria-labelledby="onboarding-video-modal"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    disableEscapeKeyDown
                    disableBackdropClick
                    disableEnforceFocus
                >
                    <Box
                        sx={{
                            position: 'relative',
                            bgcolor: 'transparent',
                            boxShadow: 'none',
                            p: 0,
                            outline: 'none',
                            maxWidth: '90%',
                            maxHeight: '90%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Simple cross (×) element */}
                        <Box
                            onClick={() => setShowOnboardingModal(false)}
                            sx={{
                                position: 'absolute',
                                right: -48,
                                top: -64,
                                color: '#fff',
                                fontSize: '60px',
                                fontWeight: 300,
                                cursor: 'pointer',
                                zIndex: 1,
                                '&:hover': {
                                    opacity: 0.8,
                                },
                                '@media (max-width: 991px)': {
                                    right: -20,
                                    top: -68,
                                },
                            }}
                        >
                            ×
                        </Box>

                        <Box
                            sx={{
                                width: '852px',
                                height: '570px',
                                position: 'relative',
                                paddingTop: '56.25%',
                                borderTopLeftRadius: 8,
                                borderTopRightRadius: 8,
                                overflow: 'hidden',
                                backgroundColor: '#000',
                                '@media (max-width: 991px)': {
                                    width: '90vw',
                                    height: 'auto',
                                },
                            }}
                        >
                            <iframe
                                src="https://www.youtube.com/embed/LVvmAvysFFk?si=kMJdA2JRUTXo6jL7"
                                title="Onboarding Video"
                                frameBorder="0"
                                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0,
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                width: '100%',
                                p: 2,
                                display: 'flex',
                                justifyContent: 'end',
                                borderBottomLeftRadius: 8,
                                borderBottomRightRadius: 8,
                            }}
                        >
                            <PinkGradientButtonWithArrow
                                label="Skip Onboarding"
                                onClick={() => setShowOnboardingModal(false)}
                            />
                        </Box>
                    </Box>
                </Modal>
            </UnsavedChangesProvider>
        </SnackbarProvider>
    );
});

export default AuthenticatedApp;
